import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import usePageTitle from '../hooks/usePageTitle';
import api from '../services/api';
import alert from '../utils/alertService';
import './SponsorDashboard.css';

const SponsorDashboard = () => {
  usePageTitle('Sponsor Dashboard');
  const [places, setPlaces] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingPlace, setEditingPlace] = useState(null);
  const [formData, setFormData] = useState({
    place_name: '',
    discount_percentage: '',
    promo_code_suffix: '',
    description: '',
    logo: null,
  });
  const [error, setError] = useState('');
  const { isSponsor } = useAuth();

  useEffect(() => {
    if (isSponsor) {
      fetchPlaces();
    }
  }, [isSponsor]);

  const fetchPlaces = async () => {
    try {
      const response = await api.get('/my-discount-place');
      setPlaces(Array.isArray(response.data) ? response.data : [response.data]);
    } catch (error) {
      console.error('Error fetching places:', error);
      setPlaces([]);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    if (e.target.type === 'file') {
      setFormData({ ...formData, logo: e.target.files[0] });
    } else {
      setFormData({ ...formData, [e.target.name]: e.target.value });
    }
  };

  // Compress image function
  const compressImage = (file, maxWidth = 1920, maxHeight = 1920, quality = 0.8) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          let width = img.width;
          let height = img.height;

          // Calculate new dimensions
          if (width > height) {
            if (width > maxWidth) {
              height = (height * maxWidth) / width;
              width = maxWidth;
            }
          } else {
            if (height > maxHeight) {
              width = (width * maxHeight) / height;
              height = maxHeight;
            }
          }

          canvas.width = width;
          canvas.height = height;

          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);

          canvas.toBlob(
            (blob) => {
              if (blob) {
                const compressedFile = new File([blob], file.name, {
                  type: 'image/jpeg',
                  lastModified: Date.now(),
                });
                console.log(`Image compressed: ${(file.size / 1024 / 1024).toFixed(2)}MB -> ${(compressedFile.size / 1024 / 1024).toFixed(2)}MB`);
                resolve(compressedFile);
              } else {
                reject(new Error('Compression failed'));
              }
            },
            'image/jpeg',
            quality
          );
        };
        img.onerror = reject;
        img.src = e.target.result;
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    try {
      console.log('=== SUBMIT START ===');
      console.log('Form Data:', formData);
      console.log('Editing Place:', editingPlace);
      
      const data = new FormData();
      data.append('place_name', formData.place_name || '');
      data.append('discount_percentage', formData.discount_percentage || '');
      
      // Always append promo_code_suffix (even if empty, backend will handle it)
      const cleanedSuffix = formData.promo_code_suffix ? formData.promo_code_suffix.toUpperCase().replace(/[^A-Z0-9-]/g, '') : '';
      data.append('promo_code_suffix', cleanedSuffix);
      
      data.append('description', formData.description || '');
      
      if (formData.logo) {
        let logoFile = formData.logo;
        if (logoFile.size > 2 * 1024 * 1024) {
          console.log('Compressing logo...', logoFile.size);
          logoFile = await compressImage(logoFile);
        }
        data.append('logo', logoFile);
      }

      // Log FormData contents
      console.log('FormData contents:');
      for (let pair of data.entries()) {
        console.log(pair[0] + ': ' + (pair[1] instanceof File ? pair[1].name : pair[1]));
      }

      if (editingPlace) {
        console.log('Updating place with ID:', editingPlace.id);
        try {
          // For PUT with FormData, Laravel needs method spoofing
          // PHP doesn't parse FormData for PUT requests, so we use POST with _method=PUT
          data.append('_method', 'PUT');
          
          const response = await api.post(`/discount-places/${editingPlace.id}`, data);
          console.log('Update Response:', response.data);
          alert.success('Discount place updated successfully!');
        } catch (updateErr) {
          console.error('Update error:', updateErr);
          console.error('Update error response:', updateErr.response);
          throw updateErr; // Re-throw to be caught by outer catch
        }
      } else {
        console.log('Creating new place');
        // Create a new axios config to override default headers for FormData
        const config = {
          headers: {
            // Don't set Content-Type - axios will set it automatically with boundary
          },
          transformRequest: (data) => {
            // Return FormData as-is
            return data;
          }
        };
        
        const response = await api.post('/discount-places', data, config);
        console.log('Create Response:', response.data);
        alert.success('Discount place created successfully!');
      }

      setShowForm(false);
      setEditingPlace(null);
      setFormData({ place_name: '', discount_percentage: '', promo_code_suffix: '', description: '', logo: null });
      console.log('Fetching places...');
      await fetchPlaces();
      console.log('=== SUBMIT END ===');
    } catch (err) {
      console.error('=== ERROR SAVING DISCOUNT PLACE ===');
      console.error('Error object:', err);
      console.error('Error response:', err.response);
      console.error('Error response data:', err.response?.data);
      console.error('Error response status:', err.response?.status);
      console.error('Error message:', err.message);
      console.error('Error stack:', err.stack);
      
      const errorMessage = err.response?.data?.message || 
                          err.response?.data?.error || 
                          (err.response?.data?.errors ? JSON.stringify(err.response.data.errors) : null) ||
                          err.message || 
                          'Error saving discount place';
      setError(errorMessage);
      alert.error(errorMessage);
      console.error('=== ERROR END ===');
    }
  };

  const handleEdit = (place) => {
    setEditingPlace(place);
    // Extract suffix from promo_code (remove "ALIENS-" prefix if exists)
    let promoCodeSuffix = '';
    if (place.promo_code) {
      promoCodeSuffix = place.promo_code.replace(/^ALIENS-?/i, '');
    }
    setFormData({
      place_name: place.place_name,
      discount_percentage: place.discount_percentage,
      promo_code_suffix: promoCodeSuffix,
      description: place.description || '',
      logo: null,
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this discount place?')) return;

    try {
      await api.delete(`/discount-places/${id}`);
      alert.success('Discount place deleted successfully!');
      fetchPlaces();
    } catch (err) {
      alert.error(err.response?.data?.message || 'Error deleting discount place');
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingPlace(null);
    setFormData({ place_name: '', discount_percentage: '', promo_code: '', description: '', logo: null });
    setError('');
  };

  if (!isSponsor) {
    return (
      <div className="unauthorized-container">
        <div className="unauthorized-message">
          <h2>🚫 Unauthorized</h2>
          <p>You are not authorized to access this page.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="loading">
        <div className="loading-spinner"></div>
        <div className="loading-text">Loading...</div>
      </div>
    );
  }

  return (
    <div className="sponsor-dashboard">
      <div className="dashboard-header">
        <h1>📢 My Discount Places</h1>
        <div className="header-actions">
          <button className="btn-primary" onClick={() => setShowForm(true)}>
            ➕ Add New Place
          </button>
        </div>
      </div>

      {showForm && (
        <div className="form-modal-overlay" onClick={handleCancel}>
          <div className="form-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="form-header">
              <h2>{editingPlace ? '✏️ Edit Place' : '✨ Add New Place'}</h2>
              <button className="btn-close" onClick={handleCancel}>✕</button>
            </div>
            
            {error && <div className="error-message">{error}</div>}
            
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>🏪 Place Name</label>
                <input
                  type="text"
                  name="place_name"
                  value={formData.place_name}
                  onChange={handleChange}
                  placeholder="Enter place name"
                  required
                />
              </div>

              <div className="form-group">
                <label>💰 Discount Percentage (%)</label>
                <input
                  type="number"
                  name="discount_percentage"
                  value={formData.discount_percentage}
                  onChange={handleChange}
                  min="0"
                  max="100"
                  step="0.01"
                  placeholder="Enter discount percentage"
                  required
                />
              </div>

              <div className="form-group">
                <label>🎟️ Promo Code</label>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                  <span style={{ 
                    color: '#00ff88', 
                    fontSize: '18px', 
                    fontWeight: '700',
                    padding: '8px 12px',
                    background: 'rgba(0, 255, 136, 0.1)',
                    border: '1px solid rgba(0, 255, 136, 0.3)',
                    borderRadius: '6px'
                  }}>
                    ALIENS-
                  </span>
                  <input
                    type="text"
                    name="promo_code_suffix"
                    value={formData.promo_code_suffix}
                    onChange={(e) => {
                      // Only allow uppercase letters, numbers, and hyphens
                      const cleaned = e.target.value.toUpperCase().replace(/[^A-Z0-9-]/g, '');
                      setFormData({ ...formData, promo_code_suffix: cleaned });
                    }}
                    placeholder="REST"
                    maxLength="20"
                    required
                    style={{ 
                      textTransform: 'uppercase',
                      flex: '1',
                      minWidth: '150px'
                    }}
                  />
                </div>
                <div style={{ marginTop: '8px' }}>
                  <small style={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: '12px', display: 'block' }}>
                    Full code: <strong style={{ color: '#00ff88' }}>ALIENS-{formData.promo_code_suffix || 'XXX'}</strong>
                  </small>
                  <small style={{ color: 'rgba(255, 255, 255, 0.5)', fontSize: '11px', display: 'block', marginTop: '4px' }}>
                    Enter only the place code (e.g., REST for restaurant). This code will be visible only to registered users.
                  </small>
                </div>
              </div>

              <div className="form-group">
                <label>📄 Description</label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  placeholder="Enter description"
                  rows="4"
                />
              </div>

              <div className="form-group">
                <label>🖼️ Logo (Optional)</label>
                <div className="file-upload-wrapper">
                  <input
                    type="file"
                    name="logo"
                    accept="image/*"
                    onChange={handleChange}
                    className="file-input"
                    id="logo-upload"
                  />
                  <label htmlFor="logo-upload" className="file-upload-label">
                    {formData.logo ? formData.logo.name : editingPlace?.logo ? 'Change Logo' : 'Choose Image'}
                  </label>
                </div>
                {editingPlace?.logo && !formData.logo && (
                  <div className="current-logo">
                    <img
                      src={`https://aliensteam.com/app-media/${editingPlace.logo}?t=${Date.now()}`}
                      alt="Current logo"
                      className="logo-preview"
                    />
                  </div>
                )}
                {formData.logo && (
                  <div className="current-logo">
                    <img
                      src={URL.createObjectURL(formData.logo)}
                      alt="New logo preview"
                      className="logo-preview"
                    />
                  </div>
                )}
              </div>

              <div className="form-actions">
                <button type="button" className="btn-secondary" onClick={handleCancel}>
                  Cancel
                </button>
                <button type="submit" className="btn-primary">
                  {editingPlace ? '💾 Update Place' : '✨ Create Place'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* QR Scanner Button - Always visible */}

      {places.length === 0 ? (
        <div className="no-data">
          <p>No discount places yet. Click "Add New Place" to create one!</p>
        </div>
      ) : (
        <div className="places-grid">
          {places.map((place) => {
            const defaultImages = [
              'https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?w=400&h=200&fit=crop&q=80&auto=format',
              'https://images.unsplash.com/photo-1555396273-367ea4eb4db5?w=400&h=200&fit=crop&q=80&auto=format',
              'https://images.unsplash.com/photo-1571091718767-18b5b1457add?w=400&h=200&fit=crop&q=80&auto=format',
            ];
            const defaultImageIndex = (place.id - 1) % defaultImages.length;
            const defaultImage = defaultImages[defaultImageIndex];
            
            const logoUrl = place.logo_url 
              ? `${place.logo_url}?t=${Date.now()}` 
              : (place.logo 
                ? `https://aliensteam.com/app-media/${place.logo}?t=${Date.now()}` 
                : defaultImage);
            
            return (
              <div key={place.id} className="place-card">
                <div className="place-image-wrapper">
                  <img
                    src={logoUrl}
                    alt={place.place_name}
                    className="place-logo"
                    onError={(e) => {
                      const currentSrc = e.target.src;
                      const retryCount = parseInt(e.target.getAttribute('data-retry') || '0');
                      console.log(`Sponsor Logo failed (retry ${retryCount}):`, currentSrc);
                      
                      if (retryCount < 2) {
                        e.target.setAttribute('data-retry', retryCount + 1);
                        if (currentSrc.includes('/storage/app/public/')) {
                          e.target.src = currentSrc.replace('/storage/app/public/', '/storage/');
                        } else if (currentSrc.includes('/storage/')) {
                          e.target.src = currentSrc.replace('/storage/', '/storage/app/public/');
                        } else if (e.target.src !== defaultImage) {
                          e.target.src = defaultImage;
                        }
                      } else if (e.target.src !== defaultImage) {
                        e.target.src = defaultImage;
                      }
                    }}
                  />
                </div>
                <div className="place-content">
                  <h3>{place.place_name}</h3>
                  <div className="discount-badge">
                    {place.discount_percentage}% OFF
                  </div>
                  {place.description && (
                    <p className="place-description">{place.description}</p>
                  )}
                  <div className="place-actions">
                    <button
                      className="btn-edit"
                      onClick={() => handleEdit(place)}
                    >
                      ✏️ Edit
                    </button>
                    <button
                      className="btn-delete"
                      onClick={() => handleDelete(place.id)}
                    >
                      🗑️ Delete
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default SponsorDashboard;

